import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Folder, FolderOpen, FileText } from 'lucide-react';

/**
 * One row of the tree. Callers build these from whatever they hold — the docs
 * navigation mixes sections and articles, the manage screen lists sections
 * only — so the tree itself stays presentational.
 */
export interface TreeNode {
    id: string;
    label: string;
    kind?: 'section' | 'article';
    /** Set to make the row a link; otherwise it calls onSelect. */
    href?: string;
    /** Rendered at the end of the row when greater than zero. */
    count?: number;
    children?: TreeNode[];
}

interface WikiTocTreeProps {
    nodes: TreeNode[];
    selectedId?: string | null;
    onSelect?: (node: TreeNode) => void;
    /** Per-row controls, rendered right of the label. */
    renderActions?: (node: TreeNode) => React.ReactNode;
    /**
     * Whether a node starts open. The manage screen wants the whole tree
     * visible; the docs navigation starts closed and opens the current path.
     */
    defaultExpanded?: boolean;
    /** Ancestors of the current page — open unless the reader closed them. */
    expandIds?: string[];
}

/**
 * Carbon-style disclosure tree: the caret expands, the label selects, and long
 * names wrap rather than being clipped.
 */
const WikiTocTree: React.FC<WikiTocTreeProps> = ({
    nodes,
    selectedId,
    onSelect,
    renderActions,
    defaultExpanded = true,
    expandIds,
}) => {
    // Only explicit reader toggles are stored, so a node the reader has not
    // touched follows the current route as it changes.
    const [overrides, setOverrides] = useState<Record<string, boolean>>({});
    const autoExpanded = new Set(expandIds || []);

    const isExpanded = (id: string) => overrides[id] ?? (defaultExpanded || autoExpanded.has(id));

    const toggle = (id: string) =>
        setOverrides(prev => ({
            ...prev,
            [id]: !(prev[id] ?? (defaultExpanded || autoExpanded.has(id))),
        }));

    const renderNodes = (items: TreeNode[], depth: number): React.ReactNode =>
        items.map(node => {
            const children = node.children || [];
            const isParent = children.length > 0;
            const expanded = isParent && isExpanded(node.id);
            const isSelected = selectedId === node.id;
            const isSection = node.kind !== 'article';

            const rowInner = (
                <>
                    <span className={`shrink-0 mt-0.5 ${isSelected ? 'text-brand-600' : 'text-gray-400'}`}>
                        {isSection ? (
                            expanded ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />
                        ) : (
                            <FileText className="w-4 h-4" />
                        )}
                    </span>
                    <span
                        className={`flex-1 min-w-0 text-sm leading-5 break-words ${
                            isSelected ? 'text-brand-700 font-medium' : 'text-gray-800'
                        }`}
                    >
                        {node.label}
                    </span>
                    {(node.count ?? 0) > 0 && (
                        <span className={`shrink-0 text-xs mt-0.5 ${isSelected ? 'text-brand-600' : 'text-gray-400'}`}>
                            {node.count}
                        </span>
                    )}
                </>
            );

            const rowClasses = 'flex-1 min-w-0 flex items-start gap-2 py-2 pr-2 text-left';

            return (
                <li key={node.id} role="none">
                    <div
                        role="treeitem"
                        aria-expanded={isParent ? expanded : undefined}
                        aria-selected={isSelected}
                        className={`flex items-start border-l-2 transition-colors ${
                            isSelected
                                ? 'bg-brand-50 border-l-brand-500'
                                : 'border-l-transparent hover:bg-gray-50'
                        }`}
                        style={{ paddingLeft: `${depth * 20}px` }}
                    >
                        {isParent ? (
                            <button
                                type="button"
                                onClick={() => toggle(node.id)}
                                aria-label={expanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
                                className="shrink-0 p-2 text-gray-500 hover:text-gray-900"
                            >
                                <ChevronRight
                                    className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                                />
                            </button>
                        ) : (
                            <span className="shrink-0 w-[30px]" aria-hidden="true" />
                        )}

                        {node.href ? (
                            <Link to={node.href} className={rowClasses}>
                                {rowInner}
                            </Link>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onSelect?.(node)}
                                disabled={!onSelect}
                                className={`${rowClasses} disabled:cursor-default`}
                            >
                                {rowInner}
                            </button>
                        )}

                        {renderActions && (
                            <div className="shrink-0 flex items-center gap-0.5 pr-2 py-1.5">
                                {renderActions(node)}
                            </div>
                        )}
                    </div>

                    {isParent && expanded && <ul role="group">{renderNodes(children, depth + 1)}</ul>}
                </li>
            );
        });

    return (
        <ul role="tree" className="py-1">
            {renderNodes(nodes, 0)}
        </ul>
    );
};

export default WikiTocTree;
